import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import MainLayout from '@/components/layout/MainLayout';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { useAppStore } from '@/store'; // Keep useAppStore for other states if needed
import { useAuth } from '@/contexts/AuthContext';
import LoadingScreen from '@/components/ui/loading-screen';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import ChatGrid from '@/components/chat/ChatGrid';
import EmptyChatsState from '@/components/chat/EmptyChatsState';
import { toast } from '@/components/ui/use-toast';
import ProjectHeader from '@/components/projects/ProjectHeader';
import { Switch } from '@/components/ui/switch';
import { isProjectOSAccessEnabled, enableProjectOSAccess, disableProjectOSAccess } from '@/lib/osIntegration';
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Info } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { FileText as DocIcon, Upload, File } from 'lucide-react';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist/build/pdf';
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.js?url';
import { useKnowledgeBaseDocuments } from '@/hooks/useKnowledgeBaseDocuments';
import ProjectAiContextEditor from '@/components/projects/ProjectAiContextEditor';

const ProjectDetail = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { projects, chats, loadingProjects, loading, addChat, refreshChats, models, updateProject } = useSupabaseStore();
  const { user } = useAuth();
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [osAccessEnabled, setOsAccessEnabled] = useState(false);
  const [osAccessLoading, setOsAccessLoading] = useState(false);
  const [projectDocs, setProjectDocs] = useState<Array<{ id: string; document_title: string; status: string; updated_at: string; file_path: string }>>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>('');
  // NEW: track uploaded storage files for this project
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ name: string; path: string; size: number; created_at?: string }>>([]);

  // NEW: Project Knowledge Base state (declare BEFORE any effects that use it)
  const [kbId, setKbId] = useState<string | null>(null);
  const [kbName, setKbName] = useState<string>('');
  const [kbDocs, setKbDocs] = useState<any[]>([]);
  const { fetchKnowledgeBaseDocuments, addDocument } = useKnowledgeBaseDocuments();

  // Set PDF worker (fixes worker error in Vite)
  GlobalWorkerOptions.workerSrc = pdfWorker;

  const project = useMemo(() => {
    // Defensive check: Ensure projects is an array before calling find
    if (!Array.isArray(projects)) {
      return undefined; 
    }
    return projects.find(p => p.id === projectId);
  }, [projects, projectId]);

  useEffect(() => {
    const init = async () => {
      if (!project) return;
      try {
        const enabled = await isProjectOSAccessEnabled(project.id, project.name);
        setOsAccessEnabled(enabled);
      } catch (e) {
        console.warn('Failed to check project OS access:', e);
      }
    };
    init();
  }, [project?.id, project?.name]);

  const projectChats = useMemo(() => {
    if (!projectId) return [];
    // Defensive check: Ensure chats is an array before calling filter
    if (!Array.isArray(chats)) {
      return [];
    }
    return chats
      .filter(chat => chat.project_id === projectId)
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [chats, projectId]);

  const defaultModel = useMemo(() => {
    if (!Array.isArray(models) || models.length === 0) {
      return undefined; // Return undefined if models are not yet loaded or empty
    }

    if (user && user.defaultModelId) {
      const found = models.find(m => m.id === user.defaultModelId);
      if (found) return found;
    }
    
    // Fallback to a free tier model or the first available model
    return models.find(m => m.tier === 'free') || models[0];
  }, [models, user]);

  useEffect(() => {
    const fetchDocs = async () => {
      if (!projectId || !user) return;
      const { data, error } = await supabase
        .from('mapleword_project_documents')
        .select('id, document_title, status, updated_at, file_path')
        .eq('user_id', user.id)
        .eq('project_id', projectId)
        .order('updated_at', { ascending: false });
      if (!error && Array.isArray(data)) {
        setProjectDocs(data);
      }
    };
    fetchDocs();
  }, [projectId, user?.id]);

  // NEW: Realtime sync for project documents
  useEffect(() => {
    if (!projectId || !user) return;

    const sortDocs = (docs: typeof projectDocs) =>
      [...docs].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

    const channel = supabase
      .channel(`mp-docs-realtime-${projectId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'mapleword_project_documents', filter: `project_id=eq.${projectId}` },
        (payload) => {
          const { eventType, new: newRow, old: oldRow } = payload as any;

          // Extra guard: ensure this row belongs to the same user
          const belongsToUser = (row: any) => row && row.user_id === user.id;

          if (eventType === 'INSERT' && newRow && belongsToUser(newRow)) {
            const doc = {
              id: newRow.id,
              document_title: newRow.document_title,
              status: newRow.status,
              updated_at: newRow.updated_at,
              file_path: newRow.file_path
            };
            setProjectDocs((prev) => {
              const exists = prev.some((d) => d.id === doc.id);
              return sortDocs(exists ? prev.map(d => d.id === doc.id ? doc : d) : [doc, ...prev]);
            });
          }

          if (eventType === 'UPDATE' && newRow && belongsToUser(newRow)) {
            const doc = {
              id: newRow.id,
              document_title: newRow.document_title,
              status: newRow.status,
              updated_at: newRow.updated_at,
              file_path: newRow.file_path
            };
            setProjectDocs((prev) => sortDocs(prev.map((d) => (d.id === doc.id ? doc : d))));
          }

          if (eventType === 'DELETE' && oldRow && belongsToUser(oldRow)) {
            setProjectDocs((prev) => prev.filter((d) => d.id !== oldRow.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [projectId, user?.id]);

  // Helper to fetch uploaded files from Supabase Storage for this project
  const fetchUploadedFiles = React.useCallback(async () => {
    if (!user?.id || !projectId) {
      setUploadedFiles([]);
      return;
    }
    const bucket = 'project-files';
    const basePath = `${user.id}/${projectId}`;

    // IMPORTANT: storage.list supports sorting by 'name' or 'updated_at' (not 'created_at')
    const { data, error } = await supabase.storage.from(bucket).list(basePath, {
      limit: 1000,
      sortBy: { column: 'updated_at', order: 'desc' }
    });

    if (error) {
      console.warn('[ProjectDetail] storage.list error:', error);
      setUploadedFiles([]);
      return;
    }

    const files = (data || [])
      .filter(f => f && typeof f.name === 'string' && !f.name.endsWith('/'))
      .map(f => ({
        name: f.name,
        path: `${bucket}/${basePath}/${f.name}`,
        // Use metadata.size (if available); default to 0
        size: (f as any).metadata?.size ?? 0,
        created_at: (f as any).created_at || (f as any).updated_at || undefined
      }));

    setUploadedFiles(files);
  }, [user?.id, projectId]);

  // Fetch uploaded files on load and when project changes
  useEffect(() => {
    fetchUploadedFiles();
  }, [fetchUploadedFiles]);

  const openInMapleOS = (title: string, filePath: string) => {
    const url = `/desktop/start?open=maple-word&title=${encodeURIComponent(`MapleWord - ${title}`)}&filePath=${encodeURIComponent(filePath)}`;
    window.open(url, '_blank');
  };

  const handleNewChat = useCallback(async () => {
    console.log('handleNewChat called');
    console.log('user:', user);
    console.log('defaultModel:', defaultModel);
    console.log('projectId:', projectId);
    console.log('isCreatingChat:', isCreatingChat);

    if (!user || !defaultModel || !projectId || isCreatingChat) {
      console.log('Pre-conditions not met for new chat creation. Exiting.');
      return;
    }
    setIsCreatingChat(true);
    try {
      const newChat = await addChat(user.id, 'New Chat', defaultModel.id, projectId);
      console.log('addChat returned:', newChat);
      if (!newChat) {
        throw new Error("Failed to create chat - no chat object returned");
      }
      navigate(`/chat/${newChat.id}`);
      console.log('Navigating to:', `/chat/${newChat.id}`);
    } catch (error) {
      console.error('Error creating new chat in project:', error);
      toast({
        title: "Error",
        description: "Failed to create a new chat. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsCreatingChat(false);
    }
  }, [user, defaultModel, projectId, isCreatingChat, addChat, refreshChats, navigate]);

  const handleToggleOSAccess = async (value: boolean) => {
    if (!project) return;
    setOsAccessLoading(true);
    try {
      if (value) {
        await enableProjectOSAccess({ id: project.id, name: project.name, description: project.description });
        await updateProject(project.id, { os_access_enabled: true });
        setOsAccessEnabled(true);
        toast({ title: 'OS Access enabled', description: 'This project will appear under Documents/Projects in the OS.' });
      } else {
        await disableProjectOSAccess({ id: project.id, name: project.name });
        await updateProject(project.id, { os_access_enabled: false });
        setOsAccessEnabled(false);
        toast({ title: 'OS Access disabled', description: 'The OS access marker was removed (files remain).' });
      }
    } catch (err: any) {
      toast({ title: 'OS Access error', description: err.message || 'Failed to toggle OS access.', variant: 'destructive' });
    } finally {
      setOsAccessLoading(false);
    }
  };

  const handleSaveAiContext = React.useCallback(async (nextValue: string | null) => {
    if (!project) return;

    await updateProject(project.id, {
      ai_context_markdown: nextValue,
      updated_at: new Date().toISOString(),
    });

    toast({
      title: 'AI context saved',
      description: 'Hidden markdown context is now available to chats in this project.',
    });
  }, [project, updateProject]);

  // Helper: ensure KB exists and return its ID
  const ensureProjectKnowledgeBaseId = React.useCallback(async () => {
    if (!user || !projectId || !project) return null;

    // Standard name (ID-based) used across the app
    const idBasedName = `Project ${projectId} KB`;

    // 1) Try ID-based KB first
    let { data: kbExisting } = await supabase
      .from('knowledge_bases')
      .select('id, name')
      .eq('created_by', user.id)
      .eq('name', idBasedName)
      .maybeSingle();

    if (kbExisting?.id) return kbExisting.id;

    // 2) Fallback to legacy name pattern by project.name (if it exists), then rename to ID-based
    const legacyName = `Project ${project.name} KB`;
    const { data: kbLegacy } = await supabase
      .from('knowledge_bases')
      .select('id, name')
      .eq('created_by', user.id)
      .eq('name', legacyName)
      .maybeSingle();

    if (kbLegacy?.id) {
      // Attempt to rename legacy KB to the standard ID-based name so it stays sticky going forward
      await supabase
        .from('knowledge_bases')
        .update({ name: idBasedName, updated_at: new Date().toISOString() })
        .eq('id', kbLegacy.id);
      return kbLegacy.id;
    }

    // 3) Create new KB with ID-based name if none exist
    const { data: kbNew, error } = await supabase
      .from('knowledge_bases')
      .insert({
        name: idBasedName,
        description: `Knowledge base for project ${project.name}`,
        organization_id: null,
        created_by: user.id,
        is_private: true,
      })
      .select('id, name')
      .single();

    if (error) return null;
    return kbNew?.id ?? null;
  }, [user, projectId, project]);

  // Initialize/load KB
  useEffect(() => {
    const initKb = async () => {
      if (!projectId || !user || !project) return;
      const id = await ensureProjectKnowledgeBaseId();
      if (!id) return;
      setKbId(id);
      // Friendly display name (does not affect lookup)
      setKbName(`Project ${project.name} KB`);
      const docs = await fetchKnowledgeBaseDocuments(id);
      setKbDocs(docs);
    };
    initKb();
  }, [projectId, user?.id, project?.name, ensureProjectKnowledgeBaseId, fetchKnowledgeBaseDocuments]);

  // Realtime updates for KB docs (optional but helpful)
  useEffect(() => {
    if (!kbId) return;
    const channel = supabase
      .channel(`kb-docs-${kbId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'knowledge_base_documents', filter: `knowledge_base_id=eq.${kbId}` },
        async () => {
          const docs = await fetchKnowledgeBaseDocuments(kbId);
          setKbDocs(docs);
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [kbId, fetchKnowledgeBaseDocuments]);

  // Helper: get clean username for OS pathing
  const getUsernameForOS = React.useCallback(async () => {
    if (!user) return 'User';
    const { data: profile } = await supabase
      .from('profiles')
      .select('username,email')
      .eq('id', user.id)
      .maybeSingle();
    const candidate = profile?.username || (profile?.email || user.email || 'User').split('@')[0] || 'User';
    return (candidate.replace(/[^a-zA-Z0-9]/g, '') || 'User');
  }, [user]);

  // Helper: mirror file into MapleOS if osAccessEnabled
  const mirrorToOS = React.useCallback(async (fileName: string, content: string, contentType: string) => {
    if (!user || !project) return;
    if (!osAccessEnabled) return;
    const username = await getUsernameForOS();
    const docsPath = `C:\\User\\${username}\\Documents\\Projects\\${project.name}\\Uploads`;
    const filePath = `${docsPath}\\${fileName}`;

    const { data: existing } = await supabase
      .from('os_user_files')
      .select('id')
      .eq('user_id', user.id)
      .eq('file_path', filePath)
      .maybeSingle();

    const payload: any = {
      user_id: user.id,
      file_path: filePath,
      file_name: fileName,
      parent_path: docsPath,
      content,
      content_type: contentType || 'text/plain',
      size_bytes: content.length,
      is_directory: false,
      updated_at: new Date().toISOString(),
    };

    if (existing?.id) {
      await supabase.from('os_user_files').update(payload).eq('id', existing.id).eq('user_id', user.id);
    } else {
      await supabase.from('os_user_files').insert(payload);
    }
  }, [user, project, osAccessEnabled, getUsernameForOS]);

  // Text extraction helpers
  const stripHtmlTags = (value: string) => value.replace(/<\/?[^>]+>/g, '');

  const extractTextFromPdf = async (arrayBuffer: ArrayBuffer): Promise<string> => {
    const pdf = await getDocument({ data: arrayBuffer }).promise;
    let text = '';
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const content = await page.getTextContent();
      const strings = content.items.map((item: any) => item.str || '').filter(Boolean);
      text += strings.join(' ') + '\n';
    }
    return text.trim();
  };

  const extractTextFromDocx = async (arrayBuffer: ArrayBuffer): Promise<string> => {
    const mammoth = await import('mammoth');
    const result = await mammoth.convertToHtml({ arrayBuffer });
    const html = (result.value || '').trim();
    return stripHtmlTags(html);
  };

  // Upload directly into KB (no separate Uploaded Files list)
  const handleUploadFilesToKB = async (files: FileList | null) => {
    if (!files || files.length === 0 || !kbId) return;
    setUploading(true);
    setUploadProgress('Preparing…');
    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        setUploadProgress(`Processing ${f.name} (${i + 1}/${files.length})…`);
        const type = (f.type || '').toLowerCase();
        const name = f.name;
        const size = f.size;

        let contentText = '';

        if (type.startsWith('text/') || name.toLowerCase().endsWith('.md') || name.toLowerCase().endsWith('.csv')) {
          contentText = await f.text();
        } else if (type === 'text/html') {
          const html = await f.text();
          contentText = stripHtmlTags(html);
        } else if (type === 'application/pdf') {
          const ab = await f.arrayBuffer();
          contentText = await extractTextFromPdf(ab);
        } else if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
          const ab = await f.arrayBuffer();
          contentText = await extractTextFromDocx(ab);
        } else {
          // Non-text (images, pptx, others) store empty content; can be extended with OCR later
          contentText = '';
        }

        // Insert into KB (hook will embed when contentText is present)
        await addDocument(kbId, {
          name,
          content: contentText,
          fileType: type || 'application/octet-stream',
          size
        });
      }

      // Refresh list
      const docs = await fetchKnowledgeBaseDocuments(kbId);
      setKbDocs(docs);
      setUploadProgress('Done.');
      toast({ title: 'Upload complete', description: 'Files were added to the project Knowledge Base.' });
    } catch (err: any) {
      console.error('[ProjectDetail] KB upload error:', err);
      toast({ title: 'Upload failed', description: err?.message || 'Could not upload files.', variant: 'destructive' });
    } finally {
      setUploading(false);
      setUploadProgress('');
    }
  };

  if (loadingProjects || loading || models.length === 0) { // Added models.length === 0 to loading condition
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <LoadingScreen />
        </div>
      </MainLayout>
    );
  }

  if (!project) {
    return (
      <MainLayout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold">Project not found</h2>
          <p className="text-muted-foreground mt-2">The project you are looking for does not exist or you do not have permission to view it.</p>
          <Button asChild className="mt-4">
            <Link to="/projects">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Projects
            </Link>
          </Button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container px-4 py-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/projects')} className="mb-4 -ml-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Projects
          </Button>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm">Enable OS Access</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-6 w-6 p-0">
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-xs">
                    When ON, this project appears inside MapleOS (Documents/Projects) and files can be mirrored for easy access. Turning OFF removes the marker but leaves existing files untouched.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Switch checked={osAccessEnabled} onCheckedChange={handleToggleOSAccess} disabled={osAccessLoading} />
          </div>
          <ProjectHeader project={project} onNewChat={handleNewChat} isCreatingChat={isCreatingChat} />
        </div>

        <div className="mb-8">
          <ProjectAiContextEditor
            projectName={project.name}
            value={project.ai_context_markdown}
            onSave={handleSaveAiContext}
          />
        </div>

        {projectChats.length > 0 ? (
          <ChatGrid chats={projectChats} />
        ) : (
          <EmptyChatsState
            title="No chats in this project yet"
            description="Get started by creating a new chat."
            showButton
            onNewChat={handleNewChat}
          />
        )}

        {/* Project Knowledge Base */}
        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-3">Knowledge Base</h2>
          <Card className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">{kbName || 'Project Knowledge Base'}</div>
                <div className="text-xs text-muted-foreground">
                  {kbDocs.length} {kbDocs.length === 1 ? 'document' : 'documents'}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Input
                  type="file"
                  multiple
                  accept=".txt,.md,.html,.csv,.pdf,.docx,.pptx,image/*"
                  onChange={(e) => handleUploadFilesToKB(e.target.files)}
                  className="max-w-sm"
                />
                <Button variant="outline" size="sm" disabled>
                  <Upload className="h-4 w-4 mr-1" /> Upload
                </Button>
                {uploading && <div className="text-xs text-muted-foreground">{uploadProgress}</div>}
              </div>
            </div>

            <div className="mt-4 space-y-2">
              {kbDocs.length === 0 ? (
                <div className="text-sm text-muted-foreground">No documents in this knowledge base yet.</div>
              ) : (
                kbDocs.map((d: any) => (
                  <div key={d.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <DocIcon className="h-4 w-4 text-muted-foreground" />
                      <div className="truncate">{d.name}</div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Updated: {new Date(d.updated_at).toLocaleString()}
                    </div>
                  </div>
                ))
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Text, PDF, and DOCX files are embedded for semantic search; images/pptx are stored without embeddings.
            </p>
          </Card>
        </div>

        {/* Documents section */}
        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-3">Documents</h2>
          {projectDocs.length === 0 ? (
            <div className="text-sm text-muted-foreground">No MapleWord documents are linked to this project yet.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {projectDocs.map((doc) => (
                <Card key={doc.id} className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="font-medium truncate">{doc.document_title}</div>
                    <span className={`text-xs px-2 py-0.5 rounded ${doc.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {doc.status}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">Updated: {new Date(doc.updated_at).toLocaleString()}</div>
                  <div className="mt-3 flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => openInMapleOS(doc.document_title, doc.file_path)}>
                      Open in MapleOS
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default ProjectDetail;